// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { get, patch } from './api';
import { ENDPOINTS } from '../utils/constants';
import type {
  AlarmApiPage,
  AlarmApiPatchRequest,
  AlarmApiPatchResponse,
  AlarmApiRecord,
} from '../types';

export interface ListAlarmsFilters {
  alarmCode?: string;
  organizationId?: string;
  organizationKey?: string;
  siteKey?: string;
  equipmentId?: string;
  equipmentKey?: string;
  equipmentType?: string;
  pointId?: string;
  pointKey?: string;
  alarmStatus?: string;
  assignedTo?: string;
  acknowledgedBy?: string;
  pageSize?: number;
  cursor?: string;
}

const FILTER_TO_QUERY_PARAM: Record<keyof ListAlarmsFilters, string> = {
  alarmCode: 'alarm_code',
  organizationId: 'organization_id',
  organizationKey: 'organization_key',
  siteKey: 'site_key',
  equipmentId: 'equipment_id',
  equipmentKey: 'equipment_key',
  equipmentType: 'equipment_type',
  pointId: 'point_id',
  pointKey: 'point_key',
  alarmStatus: 'alarm_status',
  assignedTo: 'assigned_to',
  acknowledgedBy: 'acknowledged_by',
  pageSize: 'page_size',
  cursor: 'cursor',
};

/**
 * List a site's current alarms (dbo.alarm_event_state rows), optionally filtered
 * by any of the listed columns. Results are always ordered updated_at DESC;
 * pagination is cursor-based — pass the previous response's
 * `context.next_cursor` back as `cursor` for the next page.
 */
export const listAlarms = (
  siteId: string,
  filters: ListAlarmsFilters = {}
): Promise<AlarmApiPage> => {
  const params = new URLSearchParams();
  (Object.keys(filters) as (keyof ListAlarmsFilters)[]).forEach(key => {
    const value = filters[key];
    if (value !== undefined && value !== null) {
      params.set(FILTER_TO_QUERY_PARAM[key], String(value));
    }
  });
  const qs = params.toString();
  const base = ENDPOINTS.ALARMS(siteId);
  return get<AlarmApiPage>(qs ? `${base}?${qs}` : base);
};

/**
 * Fetch a single alarm by its code. The AEM API has no single-GET endpoint, so
 * this filters the site's alarm list by `alarm_code` and returns the first row
 * (or null if none match).
 */
export const getAlarm = async (
  siteId: string,
  alarmCode: string
): Promise<AlarmApiRecord | null> => {
  const page = await listAlarms(siteId, { alarmCode, pageSize: 1 });
  return page.data[0] ?? null;
};

/** Update an alarm's status and/or assignment. At least one field is required. */
export const patchAlarm = (
  siteId: string,
  alarmCode: string,
  data: AlarmApiPatchRequest
): Promise<AlarmApiPatchResponse> =>
  patch<AlarmApiPatchResponse>(ENDPOINTS.ALARM_BY_ID(siteId, alarmCode), data);
